import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { Personnel, TrackingColumn, CastAssignment } from '../../types';
import { formatPhoneNumber } from '../../utils/validation';
import { derivePersonNameIdentity, derivePersonNameIdentityUpdate } from './name-identity';
import type { ProjectScope } from '../sync/project-scope.ts';
import { deleteCastAssignment, saveCastAssignment } from './casting-assignment-api';
import { deletePersonnelProfile } from './personnel-profile-api.ts';
import { logger } from '../../utils/logger.ts';
import { markPendingPersonnelProfileDelete } from '../sync/personnel-pending-writes.ts';
// Character now lives in ShowStructureSlice

const SELF_REPORT_PERSONNEL_FIELDS = [
    'preferredName',
    'phone',
    'emergencyName',
    'emergency',
    'emergencyPhone',
    'notes',
    'bio',
    'headshotUrl',
    'headshotFileId',
    'headshotStoragePath',
    'pronouns',
    'emailOptOut',
    'accessNeeds',
    'allergies',
    'measurementNotes',
    'notificationPreference',
] as const;

const SELF_REPORT_PERSONNEL_FIELD_SET = new Set<string>(SELF_REPORT_PERSONNEL_FIELDS);

const OPERATIONAL_PERSONNEL_FIELDS = [
    'id',
    'name',
    'givenName',
    'familyName',
    'sortName',
    'category',
    'primaryRole',
    'title',
    'isDepartmentHead',
    'department',
    'productionDuties',
    'email',
    'conflicts',
    'tracking',
    'deleted',
    'linkedUserId',
    'rosterPersonId',
    'inviteQueue',
] as const;

const OPERATIONAL_PERSONNEL_FIELD_SET = new Set<string>(OPERATIONAL_PERSONNEL_FIELDS);

function findCastAssignmentRelationship(
    assignments: CastAssignment[],
    characterId: string,
    personnelId: string
): CastAssignment | undefined {
    return assignments.find(assignment => (
        assignment.characterId === characterId
        && assignment.personnelId === personnelId
    ));
}

function hasOwn(value: object, key: string): boolean {
    return Object.hasOwn(value, key);
}

function castAssignmentUpdatesWithoutId(updates: Partial<CastAssignment>): Partial<CastAssignment> {
    const { id: _ignoredId, ...safeUpdates } = updates;
    return safeUpdates;
}

function preservedCastAssignmentMetadata(
    existing: CastAssignment,
    source: CastAssignment,
    updates: Partial<CastAssignment>
): Partial<CastAssignment> {
    const preserved: Partial<CastAssignment> = {};
    if (!hasOwn(updates, 'notes')) {
        const notes = existing.notes ?? source.notes;
        if (notes !== undefined) preserved.notes = notes;
    }
    if (!hasOwn(updates, 'lastModifiedBy')) {
        const lastModifiedBy = existing.lastModifiedBy ?? source.lastModifiedBy;
        if (lastModifiedBy !== undefined) preserved.lastModifiedBy = lastModifiedBy;
    }
    return preserved;
}

function removeCastAssignmentById(assignments: CastAssignment[], id: string): void {
    const removeIndex = assignments.findIndex(assignment => assignment.id === id);
    if (removeIndex !== -1) assignments.splice(removeIndex, 1);
}

interface CastAssignmentUpdateResult {
    assignment: CastAssignment;
    removedAssignmentId?: string;
}

function updateCastAssignmentInPlace(
    assignments: CastAssignment[],
    id: string,
    updates: Partial<CastAssignment>
): CastAssignmentUpdateResult | null {
    const assignment = assignments.find(a => a.id === id);
    if (!assignment) return null;

    const nextCharacterId = updates.characterId ?? assignment.characterId;
    const nextPersonnelId = updates.personnelId ?? assignment.personnelId;
    const safeUpdates = castAssignmentUpdatesWithoutId(updates);
    const existing = findCastAssignmentRelationship(assignments, nextCharacterId, nextPersonnelId);

    if (existing && existing.id !== id) {
        Object.assign(existing, safeUpdates, {
            characterId: nextCharacterId,
            personnelId: nextPersonnelId,
        }, preservedCastAssignmentMetadata(existing, assignment, safeUpdates));
        removeCastAssignmentById(assignments, id);
        return {
            assignment: { ...existing },
            removedAssignmentId: id,
        };
    }

    Object.assign(assignment, safeUpdates);
    return { assignment: { ...assignment } };
}

function getCastingProjectScope(state: AppState & PersonnelSlice): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

function getCastingWriteActorId(state: AppState & PersonnelSlice): string | undefined {
    return state.auth.user?.canonicalUserId ?? state.auth.user?.uid;
}

function preserveCastingWrite<T>(operation: Promise<T>, label: string): void {
    void operation.catch(error => {
        logger.error(`[personnel-store] Failed to preserve ${label}:`, error);
    });
}

function createEnsembleTokenName(actorName: string, ensembleName: string): string {
    const nameParts = actorName.trim().split(' ');
    return `${ensembleName} (${nameParts[0]!} ${nameParts.length > 1 ? `${nameParts[nameParts.length - 1]![0]}.` : ''})`.trim();
}

export function sanitizeOperationalPersonnelUpdates(updates: Partial<Personnel>): Partial<Personnel> {
    return Object.fromEntries(
        Object.entries(updates).filter(([key, value]) => (
            (value !== undefined || key === 'inviteQueue') &&
            OPERATIONAL_PERSONNEL_FIELD_SET.has(key) &&
            !SELF_REPORT_PERSONNEL_FIELD_SET.has(key)
        ))
    ) as Partial<Personnel>;
}

export function sanitizeSelfReportedPersonnelUpdates(updates: Partial<Personnel>): Partial<Personnel> {
    const sanitized = Object.fromEntries(
        Object.entries(updates).filter(([key, value]) => (
            value !== undefined &&
            SELF_REPORT_PERSONNEL_FIELD_SET.has(key) &&
            !OPERATIONAL_PERSONNEL_FIELD_SET.has(key)
        ))
    ) as Partial<Personnel>;

    if (sanitized.phone !== undefined) sanitized.phone = formatPhoneNumber(sanitized.phone);
    if (sanitized.emergencyPhone !== undefined) sanitized.emergencyPhone = formatPhoneNumber(sanitized.emergencyPhone);

    return sanitized;
}

export interface PersonnelSlice {
    personnel: Personnel[];
    // characters removed - now in showStructure.characters
    trackingColumns: TrackingColumn[];
    castAssignments: CastAssignment[];
    personnelActions: {
        // Personnel Management
        addPersonnel: (member: Partial<Personnel>) => string;
        updatePersonnel: (id: string, updates: Partial<Personnel>) => void;
        updateOperationalPersonnel: (id: string, updates: Partial<Personnel>) => void;
        updateSelfReportedPersonnel: (id: string, updates: Partial<Personnel>) => void;
        deletePersonnel: (id: string) => void;
        
        // Character actions removed - now in showStructureActions
        // addCharacter, updateCharacter, deleteCharacter moved to ShowStructureSlice
        
        // Ensemble Logic - these now mutate showStructure.characters
        assignActorToEnsemble: (
            actorId: string,
            ensembleId: string,
            role?: CastAssignment['role']
        ) => void;
        removeActorFromEnsemble: (actorId: string, ensembleId: string) => void;
        reorderPersonnel: (oldIndex: number, newIndex: number) => void;
        
        // Tracking
        addTrackingColumn: (name: string) => string;
        updateTrackingColumn: (id: string, updates: Partial<TrackingColumn>) => void;
        deleteTrackingColumn: (id: string) => void;
        updateTrackingValue: (actorId: string, columnId: string, value: boolean) => void;
        

        
        // Project scoping
        clearPersonnel: () => void;
    };
    castAssignmentActions: {
        addCastAssignment: (data: Omit<CastAssignment, 'id'>) => string | null;
        removeCastAssignment: (id: string) => void;
        updateCastAssignment: (id: string, updates: Partial<CastAssignment>) => void;
        getAssignmentsForCharacter: (characterId: string) => CastAssignment[];
        getAssignmentsForPersonnel: (personnelId: string) => CastAssignment[];
        clearCastAssignments: () => void;
    };
}

export const createPersonnelSlice: StateCreator<
    AppState & PersonnelSlice,
    [["zustand/immer", never]],
    [],
    PersonnelSlice
> = (set, get) => ({
    personnel: [],
    trackingColumns: [],
    castAssignments: [],
    personnelActions: {
        addPersonnel: (member) => {
            const id = crypto.randomUUID();
            set(state => {
                const identity = derivePersonNameIdentity(member);
                const newMember: Personnel = {
                    id,
                    category: member.category || 'cast',
                    title: member.title || '',
                    email: member.email || '',
                    phone: formatPhoneNumber(member.phone || ''),
                    productionDuties: member.productionDuties || [],
                    conflicts: member.conflicts || [],
                    notes: member.notes || '',
                    // Spread remaining fields but filter out undefined values
                    ...Object.fromEntries(
                        Object.entries(member).filter(([_, v]) => v !== undefined)
                    ),
                    ...identity,
                } as Personnel;
                newMember.phone = formatPhoneNumber(newMember.phone || '');
                if (newMember.emergencyPhone !== undefined) {
                    newMember.emergencyPhone = formatPhoneNumber(newMember.emergencyPhone);
                }
                state.personnel.push(newMember);
            });
            return id;
        },
        updatePersonnel: (id, updates) => set(state => {
            const member = state.personnel.find(m => m.id === id);
            if (member) {
                const sanitized = { ...updates };
                if (sanitized.phone !== undefined) sanitized.phone = formatPhoneNumber(sanitized.phone);
                if (sanitized.emergencyPhone !== undefined) sanitized.emergencyPhone = formatPhoneNumber(sanitized.emergencyPhone);
                Object.assign(member, sanitized, derivePersonNameIdentityUpdate(member, sanitized));
            } else {
                console.warn('[PersonnelStore] Member not found:', id);
            }
        }),
        updateOperationalPersonnel: (id, updates) => set(state => {
            const member = state.personnel.find(m => m.id === id);
            if (member) {
                const sanitized = sanitizeOperationalPersonnelUpdates(updates);
                Object.assign(member, sanitized, derivePersonNameIdentityUpdate(member, sanitized));
            } else {
                console.warn('[PersonnelStore] Member not found:', id);
            }
        }),
        updateSelfReportedPersonnel: (id, updates) => set(state => {
            const member = state.personnel.find(m => m.id === id);
            if (member) {
                Object.assign(member, sanitizeSelfReportedPersonnelUpdates(updates));
            } else {
                console.warn('[PersonnelStore] Member not found:', id);
            }
        }),
        deletePersonnel: (id) => {
            const currentState = get();
            const scope = getCastingProjectScope(currentState);
            const actorId = getCastingWriteActorId(currentState);
            const memberToDelete = currentState.personnel.find(person => person.id === id);
            if (!memberToDelete) return;

            const deletedAssignmentIds = memberToDelete.category === 'cast'
                ? currentState.castAssignments
                    .filter(assignment => assignment.personnelId === id)
                    .map(assignment => assignment.id)
                : [];

            if (scope) {
                markPendingPersonnelProfileDelete(scope, id);
            }

            set(state => {
                // Remove member
                const idx = state.personnel.findIndex(c => c.id === id);
                if (idx === -1) return;
                const member = state.personnel[idx]!;
                state.personnel.splice(idx, 1);

                // Remove ensemble tokens referencing this actor
                if (member.category === 'cast') {
                    // Find tokens that belong to this actor via CastAssignment
                    const actorAssignments = state.castAssignments.filter(a => a.personnelId === id);
                    const actorCharIds = new Set(actorAssignments.map(a => a.characterId));
                    state.showStructure.characters = state.showStructure.characters.filter(
                        char => !(char.parentId && actorCharIds.has(char.id))
                    );
                    // Remove CastAssignment records
                    state.castAssignments = state.castAssignments.filter(a => a.personnelId !== id);
                }
            });

            if (scope) {
                for (const assignmentId of deletedAssignmentIds) {
                    preserveCastingWrite(
                        deleteCastAssignment(scope, assignmentId, { actorId }),
                        'cast assignment delete'
                    );
                }
                preserveCastingWrite(
                    deletePersonnelProfile(scope, id, { actorId }),
                    'personnel profile delete'
                );
            }
        },
        
        // Character CRUD moved to ShowStructureSlice:
        // Use showStructureActions.updateCharacter, showStructureActions.setCharacters, etc.
        
        assignActorToEnsemble: (actorId, ensembleId, role = 'primary') => {
            let createdAssignment: CastAssignment | null = null;
            set(state => {
                const actor = state.personnel.find(c => c.id === actorId);
                const ensemble = state.showStructure.characters.find(c => c.id === ensembleId && c.isEnsemble);
                if (!actor || !ensemble) return;

                const exists = state.castAssignments.some(assignment => {
                    if (assignment.personnelId !== actorId) return false;
                    if (assignment.characterId === ensembleId) return true;
                    return state.showStructure.characters.some(
                        character => character.id === assignment.characterId && character.parentId === ensembleId
                    );
                });
                if (exists) return;

                const tokenId = crypto.randomUUID();
                state.showStructure.characters.push({
                    id: tokenId,
                    name: createEnsembleTokenName(actor.name, ensemble.name),
                    parentId: ensembleId,
                    isEnsemble: false,
                    sceneIds: [...(ensemble.sceneIds || [])]
                });

                createdAssignment = {
                    id: crypto.randomUUID(),
                    characterId: tokenId,
                    personnelId: actorId,
                    role,
                };
                state.castAssignments.push(createdAssignment);
            });

            const currentState = get();
            const scope = getCastingProjectScope(currentState);
            const assignmentToPersist = createdAssignment as CastAssignment | null;
            if (scope && assignmentToPersist) {
                preserveCastingWrite(
                    saveCastAssignment(scope, currentState.castAssignments, assignmentToPersist, {
                        actorId: getCastingWriteActorId(currentState),
                    }),
                    'ensemble cast assignment'
                );
            }
        },
        removeActorFromEnsemble: (actorId, ensembleId) => {
            const deletedAssignmentIds: string[] = [];
            set(state => {
                const matchingAssignments = state.castAssignments.filter(assignment => {
                    if (assignment.personnelId !== actorId) return false;
                    if (assignment.characterId === ensembleId) return true;
                    return state.showStructure.characters.some(
                        character => character.id === assignment.characterId && character.parentId === ensembleId
                    );
                });
                const removedCharacterIds = new Set(
                    matchingAssignments.map(assignment => assignment.characterId)
                );
                deletedAssignmentIds.push(...matchingAssignments.map(assignment => assignment.id));

                state.showStructure.characters = state.showStructure.characters.filter(
                    character => !(character.parentId === ensembleId && removedCharacterIds.has(character.id))
                );
                state.castAssignments = state.castAssignments.filter(
                    assignment => !deletedAssignmentIds.includes(assignment.id)
                );
            });

            const currentState = get();
            const scope = getCastingProjectScope(currentState);
            if (scope) {
                const writeActorId = getCastingWriteActorId(currentState);
                for (const assignmentId of deletedAssignmentIds) {
                    preserveCastingWrite(
                        deleteCastAssignment(scope, assignmentId, { actorId: writeActorId }),
                        'ensemble cast assignment delete'
                    );
                }
            }
        },
        reorderPersonnel: (oldIndex, newIndex) => set(state => {
            if (oldIndex < 0 || newIndex < 0 || oldIndex >= state.personnel.length || newIndex >= state.personnel.length) return;
            const [moved] = state.personnel.splice(oldIndex, 1);
            state.personnel.splice(newIndex, 0, moved!);
        }),
        addTrackingColumn: (name) => {
            const id = crypto.randomUUID();
            set(state => {
                state.trackingColumns.push({
                    id,
                    name,
                    type: 'checkbox'
                });
            });
            return id;
        },
        updateTrackingColumn: (id, updates) => set(state => {
            const col = state.trackingColumns.find(c => c.id === id);
            if (col) Object.assign(col, updates);
        }),
        deleteTrackingColumn: (id) => set(state => {
            const col = state.trackingColumns.find(c => c.id === id);
            if (col) {
                col.deleted = true;
                col.deletedAt = Date.now();
            }
        }),
        updateTrackingValue: (actorId, columnId, value) => set(state => {
            const actor = state.personnel.find(c => c.id === actorId);
            if (actor) {
                if (!actor.tracking) actor.tracking = {};
                actor.tracking[columnId] = value;
            }
        }),

        // Legacy assignCharacterToActor / removeCharacterFromActor removed
        // Casting is now managed exclusively via castAssignmentActions
        
        clearPersonnel: () => set(state => {
            state.personnel = [];
            state.trackingColumns = [];
            state.castAssignments = [];
        })
    },
    castAssignmentActions: {
        addCastAssignment: (data) => {
            const existing = findCastAssignmentRelationship(
                get().castAssignments,
                data.characterId,
                data.personnelId
            );
            let assignmentToPersist: CastAssignment | null = null;

            if (existing) {
                set(state => {
                    const assignment = state.castAssignments.find(a => a.id === existing.id);
                    if (assignment) {
                        Object.assign(assignment, data);
                        assignmentToPersist = { ...assignment };
                    }
                });
                const currentState = get();
                const scope = getCastingProjectScope(currentState);
                if (scope && assignmentToPersist) {
                    preserveCastingWrite(
                        saveCastAssignment(scope, currentState.castAssignments, assignmentToPersist, {
                            actorId: getCastingWriteActorId(currentState),
                        }),
                        'cast assignment'
                    );
                }
                return existing.id;
            }

            const id = crypto.randomUUID();
            set(state => {
                assignmentToPersist = { id, ...data };
                state.castAssignments.push(assignmentToPersist);
            });
            const currentState = get();
            const scope = getCastingProjectScope(currentState);
            if (scope && assignmentToPersist) {
                preserveCastingWrite(
                    saveCastAssignment(scope, currentState.castAssignments, assignmentToPersist, {
                        actorId: getCastingWriteActorId(currentState),
                    }),
                    'cast assignment'
                );
            }
            return id;
        },
        removeCastAssignment: (id) => {
            let didRemove = false;
            set(state => {
                const idx = state.castAssignments.findIndex(a => a.id === id);
                if (idx !== -1) {
                    state.castAssignments.splice(idx, 1);
                    didRemove = true;
                }
            });
            const currentState = get();
            const scope = getCastingProjectScope(currentState);
            if (scope && didRemove) {
                preserveCastingWrite(
                    deleteCastAssignment(scope, id, {
                        actorId: getCastingWriteActorId(currentState),
                    }),
                    'cast assignment delete'
                );
            }
        },
        updateCastAssignment: (id, updates) => {
            let updateResult: CastAssignmentUpdateResult | null = null;
            set(state => {
                updateResult = updateCastAssignmentInPlace(state.castAssignments, id, updates);
            });
            const currentState = get();
            const scope = getCastingProjectScope(currentState);
            const persistedUpdate = updateResult as CastAssignmentUpdateResult | null;
            if (scope && persistedUpdate) {
                const actorId = getCastingWriteActorId(currentState);
                const operations: Promise<unknown>[] = [
                    saveCastAssignment(scope, currentState.castAssignments, persistedUpdate.assignment, {
                        actorId,
                    }),
                ];
                if (persistedUpdate.removedAssignmentId) {
                    operations.push(deleteCastAssignment(scope, persistedUpdate.removedAssignmentId, { actorId }));
                }
                preserveCastingWrite(
                    Promise.all(operations),
                    'cast assignment'
                );
            }
        },
        getAssignmentsForCharacter: (characterId) => get().castAssignments.filter(a => a.characterId === characterId),
        getAssignmentsForPersonnel: (personnelId) => get().castAssignments.filter(a => a.personnelId === personnelId),
        clearCastAssignments: () => set(state => {
            state.castAssignments = [];
        }),
    },
});
